import React, { useEffect, useRef, useState } from 'react';
import './Puzzle.css';
import { PuzzleProps } from '../../app/models/puzzleProps';
import { FormStateProps } from '../../app/models/formStateProps';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Grid, Modal, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import Swal from 'sweetalert2';
import {v4 as uuid} from "uuid";
import agent from '../../app/api/agent';
import { Player } from '../../app/models/player';
import { env } from '../../app/utils/env';
import { validateImageUrl } from '../../app/utils/validateImages';
import parse from 'html-react-parser';
import UserDataModal, { UserDataValues } from '../UserDataModal';
import { toast } from 'react-toastify';

const puzzlePlaceholder = `${process.env.PUBLIC_URL}/assets/puzzle-test.jpg`;

interface PuzzleGameProps {
  isActive?: boolean;
  collectUserData: number;
  puzzle: PuzzleProps["puzzle"];
  formState: FormStateProps['formState'];
  campaignId: string;
  campaignTitle: string;
  modalText?: string;
  modalBg?: string;
}

interface Config {
  rows: number;
  cols: number;
  image: string;
  size: {
    width: number;
    height: number;
  };
  shuffleMoves: number;
  animationTime: number;
  showNumbers: boolean;
}

const Puzzle: React.FC<PuzzleGameProps> = ({ isActive, collectUserData, puzzle, formState, campaignId, campaignTitle, modalText, modalBg }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [validatedImageUrl, setValidatedImageUrl] = useState<string>(puzzlePlaceholder);
  const [timeLeft, setTimeLeft] = useState(puzzle.hasCounter ? puzzle.puzzleDuration * 60 : null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [gameEnded, setGameEnded] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [player, setPlayer] = useState<Player | null>(null);

  // Preview overlay: show full image for 5s, fade, then start game
  const [gameStarted, setGameStarted] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewOpacity, setPreviewOpacity] = useState(1);


  const initialFormValues: any = {
    firstName: "",
    lastName: "",
    email: formState.formEmail ? "" : undefined,
    phone: formState.formPhone ? "" : undefined,
    company: formState.formCompany ? "" : undefined,
    gender: formState.formGender ? 1 : undefined,
    formAddPermissionChecked: false,
    formAddPermission2Checked: false,
    ...(formState.bonusField && { [formState.bonusField]: "" }),
  };
  const [formValues, setFormValues] = useState(initialFormValues);

  const [dataCollected, setDataCollected] = useState(false);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(true);

  let playerId = null;

  useEffect(() => {
    if (isActive === false) {
      Swal.fire({
        title: "Kampanya Aktif Değil",
        text: "Bu kampanya şu anda aktif değil. Daha sonra tekrar deneyin.",
        icon: "warning",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showCloseButton: false,
        showConfirmButton: false,
        didClose: () => {
          // prevent game from starting
          setGameEnded(true);
          setGameStarted(false);
        }
      });
    }
  }, [isActive]);

  // When image is ready, show the full image for 5s then fade -> start game
  useEffect(() => {
    if (loading || !validated) return;

    if (isActive === false) return;

    // reset counters in case user replays
    setElapsedSeconds(0);
    if (puzzle.hasCounter) {
      setTimeLeft(puzzle.puzzleDuration * 60);
    }

    setPreviewVisible(true);
    setPreviewOpacity(1);

    // show 5s, then fade out (~600ms), then start the game
    const showT = setTimeout(() => {
      setPreviewOpacity(0);
      const fadeT = setTimeout(() => {
        setPreviewVisible(false);
        setGameStarted(true); // actual game begins here
      }, 600);
      return () => clearTimeout(fadeT);
    }, 5000);

    return () => clearTimeout(showT);
  }, [loading, validated, puzzle.hasCounter, puzzle.puzzleDuration]);

  useEffect(() => {
    const validateImages = async () => {
      const imgPath = puzzle.puzzleImage?.startsWith('http') 
        ? puzzle.puzzleImage 
        : env + puzzle.puzzleImage;

      const isValidImage = puzzle.puzzleImage && await validateImageUrl(imgPath);

      if (isValidImage) {
        setValidatedImageUrl(imgPath);
      } else {
        setValidatedImageUrl(puzzlePlaceholder);
      }

      setValidated(true);
      setLoading(false);
    };

    if (!validated) {
      validateImages();
    }
  }, [validated]);

  useEffect(() => {
    if (gameEnded || !gameStarted) return;

    const timer = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);

      if (puzzle.hasCounter) {
        setTimeLeft((prev) => {
          if (prev === null) return null;
          if (prev <= 1) {
            clearInterval(timer);
            setGameEnded(true);
            handleGameOver(false); // lose condition
            return 0;
          }
          return prev - 1;
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [gameEnded, gameStarted, puzzle.hasCounter]);

  const handleGameOver = async (won: boolean) => {
    setGameEnded(true);

    Swal.fire({
      title: won ? "Oyun Tamamlandı" : "Süre Doldu",
      text: won
        ? modalText ?? `Tebrikler! Oyunu tamamladınız!`
        : "Üzgünüz, süreniz doldu. Lütfen tekrar deneyiniz.",
      icon: won ? "success" : "error",
      showCancelButton: collectUserData === 3,
      showConfirmButton: collectUserData === 3,
      confirmButtonText: "Kullanıcı Formu",
      cancelButtonText: "Başa Dön",
      background: modalBg ? `url(${env + modalBg})` : '#fff',
      customClass: {
        popup: modalBg ? 'swal-container swal-bg' : 'swal-container',
        icon: modalBg ? 'd-none' : 'swal-icon',
        title: modalBg && 'd-none',
        htmlContainer: modalBg && 'd-none'
      },
      didOpen: () => {
        if (won && collectUserData != 1) {
          document.addEventListener('click', () => {
            Swal.close();
          });
        }
      },
    }).then(async (result) => {
      playerId = uuid();
      const gameData = {
        id: uuid(),
        gameType: 2,
        prize: `${campaignTitle} hediyesi`,
        isPrize: won,
        hasWon: won,
        datePlayed: new Date().toISOString(),
        campaignId: campaignId,
        timeCompleted: elapsedSeconds,
        playerId: playerId,
        player: buildPlayerData(formValues, formState, campaignId, playerId)
      };

      if (collectUserData === 2 && player != null) {
        gameData.player = player;
        gameData.playerId = player.id;
      }

      setPlayer(gameData.player);
      await agent.Games.create(gameData);

      if (collectUserData == 3 && result.isConfirmed) {
        setOpenModal(true);
      } else {
        setPlayer(null);
        setDataCollected(false);
        resetGame();
      }
    });
  };

  const resetGame = () => {
    setTimeLeft(puzzle.hasCounter ? puzzle.puzzleDuration * 60 : null);
    setElapsedSeconds(0);
    setGameEnded(false);
    setOpenModal(false);
    setPlayer(null);
    setGameStarted(false);
    setPreviewVisible(false);
    setPreviewOpacity(1);
    setFormValues(initialFormValues);
    setDataCollected(false);

    const initial = createMatrix();
    const { shuffled, empty } = shuffleMatrix(initial);
    setMatrix(shuffled);
    setEmptyPos(empty);

    setLoading(true);
    setValidated(false);
  };

  const handleClose = () => {
    if (collectUserData != 2 && !dataCollected) {
      setPlayer(null);
    }
    setOpenModal(false);
    setFormValues(initialFormValues);
    if (gameEnded) {
      resetGame();
    }
  };

  const buildPlayerData = (formValues: any, formState: FormStateProps['formState'], campaignId: string, playerId: string) => {
    return {
      id: playerId,
      campaignId,
      firstName: formValues.firstName || "",
      lastName: formValues.lastName || "",
      gender: formState.formGender ? formValues.gender === 1 ? 1 : 2 : 0,
      email: formState.formEmail ? formValues.email || "" : "",
      phone: formState.formPhone ? formValues.phone || "" : "",
      company: formState.formCompany ? formValues.company || "" : "",
      formAddPermissionChecked: formState.formAddPermission ? formValues.formAddPermissionChecked || false : false,
      formAddPermission2Checked: formState.formAddPermission2 ? formValues.formAddPermission2Checked || false : false,
      bonusField: formState.bonusField ? formValues[formState.bonusField] || "" : "",
      bonusFieldLabel: collectUserData != 1 ? formState.bonusField || "" : "",
    };
  };  

  const hasDuplicatePlayer = async (campaignId: string, values: UserDataValues) => {
    if (!formState.duplicateCheck) return false;
    const res = await agent.Games.exists(campaignId, {
      firstName: values.firstName,
      lastName: values.lastName,
      email: formState.formEmail ? values.email : undefined,
      phone: formState.formPhone ? values.phone : undefined,
    });
    return res.duplicate;
  };

  const handleSubmit = async (values: UserDataValues) => {
    if (await hasDuplicatePlayer(campaignId, values)) {
      toast.error("Aynı oyuncu bir kampanyada sadece bir kez oynayabilir.");
      return false; // keep modal open, do NOT reset anything
    }

    const playerId = player?.id || uuid();
    const playerData = buildPlayerData(formValues, formState, campaignId, playerId);
    
    if (collectUserData === 2) {
      setPlayer(playerData);
      setDataCollected(true);
    } else {
      await agent.Players.update(playerData);
    }

    return true;
  };

  const getRowsAndCols = (tileAmount: number) => {
    switch (tileAmount) {
      case 3: return { rows: 3, cols: 3 }; 
      case 4: return { rows: 4, cols: 4 }; 
      default: return { rows: 3, cols: 3 };
    }
  };

  const { rows, cols } = getRowsAndCols(puzzle.tileAmount);
  const imageUrl = validatedImageUrl;
  const size = { width: puzzle.puzzleDiameter, height: puzzle.puzzleDiameter };

  const config: Config = {
    rows,
    cols,
    image: imageUrl,
    size,
    shuffleMoves: 300,
    animationTime: 0.2,
    showNumbers: false,
  };

  type Matrix = number[][];
  const [matrix, setMatrix] = useState<Matrix>([]);
  const [emptyPos, setEmptyPos] = useState<[number, number]>([
    config.rows - 1,
    config.cols - 1,
  ]);

  const tileWidth = config.size.width / config.cols;
  const tileHeight = config.size.height / config.rows;

  const createMatrix = (): Matrix => {
    let count = 1;
    const arr: Matrix = [];
    for (let y = 0; y < config.rows; y++) {
      const row: number[] = [];
      for (let x = 0; x < config.cols; x++) {
        row.push(count);
        count++;
      }
      arr.push(row);
    }
    arr[config.rows - 1][config.cols - 1] = 0; // boş kare
    return arr;
  };

  const shuffleMatrix = (mat: Matrix): { shuffled: Matrix; empty: [number, number] } => {
    const newMat: Matrix = mat.map((row) => [...row]);

    let empty: [number, number] = [config.rows - 1, config.cols - 1];

    for (let y = 0; y < config.rows; y++) {
      for (let x = 0; x < config.cols; x++) {
        if (newMat[y][x] === 0) {
          empty = [y, x];
          break;
        }
      }
    }

    for (let i = 0; i < config.shuffleMoves; i++) {
      const directions: [number, number][] = [
        [0, -1],
        [0, 1],
        [-1, 0],
        [1, 0],
      ];

      const validMoves = directions.filter(([dy, dx]) => {
        const [y, x] = [empty[0] + dy, empty[1] + dx];
        return y >= 0 && y < config.rows && x >= 0 && x < config.cols;
      });

      const [dy, dx] = validMoves[Math.floor(Math.random() * validMoves.length)];
      const [ty, tx] = [empty[0] + dy, empty[1] + dx];

      newMat[empty[0]][empty[1]] = newMat[ty][tx];
      newMat[ty][tx] = 0;
      empty = [ty, tx];
    }

    return { shuffled: newMat, empty };
  };

  const isPuzzleSolved = (mat: Matrix): boolean => {
    let count = 1;
    for (let y = 0; y < config.rows; y++) {
      for (let x = 0; x < config.cols; x++) {
        if (y === config.rows - 1 && x === config.cols - 1) {
          if (mat[y][x] !== 0) return false;
        } else {
          if (mat[y][x] !== count) return false;
          count++;
        }
      }
    }
    return true;
  };

  const moveTile = (y: number, x: number): void => {
    if (!gameStarted || gameEnded || previewVisible) return;
    
    const [ey, ex] = emptyPos;
    const isAdjacent =
      (y === ey && Math.abs(x - ex) === 1) || (x === ex && Math.abs(y - ey) === 1);

    if (isAdjacent) {
      const newMat: Matrix = matrix.map((row) => [...row]);
      newMat[ey][ex] = newMat[y][x];
      newMat[y][x] = 0;
      setMatrix(newMat);
      setEmptyPos([y, x]);
      
      if (isPuzzleSolved(newMat)) {
        handleGameOver(true);
      }
    }
  };

  useEffect(() => {
    const initial = createMatrix();
    const { shuffled, empty } = shuffleMatrix(initial);
    setMatrix(shuffled);
    setEmptyPos(empty);
  }, []);

  return (
    <>
      {puzzle.hasCounter && (
        <Typography variant="h6" align="center" sx={{ mb: 2, fontSize: '4rem' }}>
          {Math.floor((timeLeft ?? 0) / 60)}:{(timeLeft ?? 0) % 60 < 10 ? '0' : ''}{(timeLeft ?? 0) % 60}
        </Typography>
      )}
      {!loading && (
        <div
          ref={containerRef}
          id="puzzle-container"
          style={{  
            width: config.size.width,
            height: config.size.height,
            position: 'relative',
            margin: '0',
            backgroundColor: '#fff',
            // top: `${puzzleFromTop}%`,
          }}
        >
          {matrix.map((row, y) =>
            row.map((value, x) => {
              if (value === 0) return null;
              const bgX = (value - 1) % config.cols;
              const bgY = Math.floor((value - 1) / config.cols);
              return (
                <div
                  key={value}
                  className="tile"
                  style={{
                    width: tileWidth,
                    height: tileHeight,
                    left: x * tileWidth,
                    top: y * tileHeight,
                    backgroundImage: `url(${config.image})`,
                    backgroundSize: `${config.size.width}px ${config.size.height}px`,
                    backgroundPosition: `-${bgX * tileWidth}px -${bgY * tileHeight}px`,
                    position: 'absolute',
                    transition: `top ${config.animationTime}s, left ${config.animationTime}s`,
                  }}
                  onClick={() => moveTile(y, x)}
                >
                  {config.showNumbers && (
                    <span className="tile-number">{value}</span>
                  )}
                </div>
              );
            })
          )}
          {/* FULL IMAGE PREVIEW OVERLAY */}
          {previewVisible && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${config.image})`,
                backgroundSize: `${config.size.width}px ${config.size.height}px`,
                backgroundPosition: 'center',
                opacity: previewOpacity,
                transition: 'opacity 0.6s ease',
                zIndex: 5,
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      )}
            
      <UserDataModal
        open={collectUserData === 2 && !dataCollected ? true : openModal}
        onClose={handleClose}
        formState={formState}
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        submitLabel="Gönder"
      />
    </>
  );
};

export default Puzzle;